import {
  BottomSheet,
  Button,
  Grid,
  Heading,
  Paragraph,
  Box,
} from '@deckee/deck-hand';
import { convertDEGToDDM } from '@deckee/utilities';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector, connect } from 'react-redux';
import {
  selectUnitsConfig,
  moveMapToLocation,
  setFloatingPane,
  showControlPaneReset,
  showSelectMapPoint,
} from '../../../services/store/modules/app-controls';
import { formatLatLon } from '../../helpers/coordinate-helpers';
import { fitMapToPlan } from '../../../services/map-layer/custom-layers/trip-plan';
import DeprecatedButton from '../../shared/_deprecated/elements/Button/Button';
import Text from '../../shared/_deprecated/elements/Text/Text';
import styles from './MapPointSelector.scss';
import { hasFeatureEnabled } from '../../../services/feature';

const cx = classNames.bind(styles);

const MapPointSelector = (props) => {
  const {
    className,
    dispatchMoveMapToLocation,
    dispatchSetFloatingPane,
    dispatchShowControlPaneReset,
    dispatchShowSelectMapPoint,
    initialLatLon,
    initialZoom,
    onComplete,
    currentMapCenter,
    instructionText,
    buttonLabel,
    editPointRef,
    fitBoundsPoints,
    updatedUI,
    isLoading: parentIsLoading,
    render,
  } = props;

  const [isLoading, setIsLoading] = useState(parentIsLoading);

  const unitsConfig = useSelector(selectUnitsConfig);

  // update loading state if parent component passes new value in
  useEffect(() => {
    setIsLoading(parentIsLoading);
  }, [parentIsLoading]);

  // Fire off our initial route config
  useEffect(() => {
    dispatchSetFloatingPane();
    dispatchShowControlPaneReset();
  }, [dispatchSetFloatingPane, dispatchShowControlPaneReset]);

  useEffect(() => {
    dispatchSetFloatingPane();
    dispatchShowControlPaneReset();
    dispatchShowSelectMapPoint(editPointRef);

    setIsLoading(false);
  }, [
    dispatchSetFloatingPane,
    dispatchShowControlPaneReset,
    dispatchShowSelectMapPoint,
    editPointRef,
  ]);

  // Move map to initial edit position
  useEffect(() => {
    const setInitialMapPosition = async () => {
      await dispatchMoveMapToLocation();
      const moveMapToLatLon = async (latLon) => {
        await dispatchMoveMapToLocation({
          center: {
            lat: latLon?.lat,
            lon: latLon?.lon,
          },
          zoom: initialZoom,
        });
      };

      if (fitBoundsPoints && fitBoundsPoints.length > 1) {
        fitMapToPlan(fitBoundsPoints);
      } else if (initialLatLon?.lat && initialLatLon?.lon) {
        await moveMapToLatLon(initialLatLon);
      }
    };

    setInitialMapPosition();
  }, [
    initialLatLon,
    editPointRef,
    fitBoundsPoints,
    dispatchMoveMapToLocation,
    initialZoom,
  ]);

  const handleOnComplete = () => {
    const newLatLon = {
      lat: currentMapCenter?.lat,
      lon: currentMapCenter?.lon,
    };

    onComplete(newLatLon);
  };

  const viewClasses = cx('map-point-selector', {
    [className]: className,
  });

  return (
    <>
      {updatedUI === false && (
        <div id="mapPointSelector" className={viewClasses}>
          <Text
            className={cx('map-point-selector__instruction-text')}
            size="0"
            type="alternate-light"
            align="center"
            textAlign="center"
          >
            {instructionText}
          </Text>

          <Text
            className={cx('map-point-selector__current-lat-lon')}
            size="2"
            type="light"
            align="center"
            strong
          >
            {`${convertDEGToDDM(
              currentMapCenter?.lat,
              true,
            )}, ${convertDEGToDDM(currentMapCenter?.lon, false)}`}
          </Text>

          <DeprecatedButton
            key="yes"
            className={cx('map-point-selector__button')}
            action={handleOnComplete}
            type="secondary"
            size="large"
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? 'Loading...' : buttonLabel}
          </DeprecatedButton>
        </div>
      )}

      {updatedUI === true && (
        <BottomSheet bg="white">
          {instructionText && (
            <BottomSheet.Heading>
              <Paragraph textAlign="center">{instructionText}</Paragraph>
            </BottomSheet.Heading>
          )}

          <BottomSheet.Content>
            {render ? (
              render({
                unitsConfig,
                currentMapCenter: {
                  lat: +currentMapCenter?.lat,
                  lon: +currentMapCenter?.lon,
                },
                isLoading,
                handleOnComplete,
                buttonLabel,
              })
            ) : (
              <Grid gutters={0} margins={0}>
                <Grid.Column>
                  <Heading level={5} textAlign="center">
                    {`${formatLatLon(
                      unitsConfig,
                      +currentMapCenter?.lat,
                      +currentMapCenter?.lon,
                    )}`}
                  </Heading>
                </Grid.Column>

                <Grid.Column>
                  <Button onClick={handleOnComplete} disabled={isLoading} wide>
                    {isLoading ? 'Loading...' : buttonLabel}
                  </Button>
                </Grid.Column>
              </Grid>
            )}
          </BottomSheet.Content>
        </BottomSheet>
      )}
    </>
  );
};
MapPointSelector.propTypes = {
  dispatchMoveMapToLocation: PropTypes.func.isRequired,
  dispatchSetFloatingPane: PropTypes.func.isRequired,
  dispatchShowControlPaneReset: PropTypes.func.isRequired,
  dispatchShowSelectMapPoint: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,

  // Render prop which gets appropriate parameters passed through
  // eslint-disable-next-line react/forbid-prop-types
  render: PropTypes.any,
  editPointRef: PropTypes.number,
  fitBoundsPoints: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),

  currentMapCenter: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),

  initialZoom: PropTypes.number,
  initialLatLon: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),

  instructionText: PropTypes.string,
  buttonLabel: PropTypes.string,

  /** Extra classes to pass through */
  className: PropTypes.string,

  isLoading: PropTypes.bool,
  updatedUI: PropTypes.bool,
};
MapPointSelector.defaultProps = {
  editPointRef: 1,
  fitBoundsPoints: [],
  initialZoom: null,
  initialLatLon: {},
  instructionText: null,
  buttonLabel: null,
  currentMapCenter: null,
  className: null,
  updatedUI: false,
  isLoading: true,
  render: undefined,
};

const mapStateToProps = (state) => ({
  currentMapCenter: state.appControls.currentMapParams.viewPortDetails.center,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchSetFloatingPane: () => dispatch(setFloatingPane()),
  dispatchShowControlPaneReset: () => dispatch(showControlPaneReset()),

  dispatchShowSelectMapPoint: (pointRef) =>
    dispatch(showSelectMapPoint(pointRef)),
  dispatchMoveMapToLocation: async (newLocation) =>
    dispatch(await moveMapToLocation(newLocation)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapPointSelector);
